import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useAuthPrompt } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FolderKanban,
  Plus,
  DollarSign,
  TrendingUp,
  Clock,
  Trash2,
  Edit,
  X,
  Loader2,
  Calendar,
  Target,
  BarChart3,
  Play,
  Pause,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const statusConfig = {
  planning: { label: 'Planning', color: 'bg-slate-500/10 text-slate-500 border-slate-500/30', icon: Target },
  active: { label: 'Active', color: 'bg-blue-500/10 text-blue-500 border-blue-500/30', icon: Play },
  on_hold: { label: 'On Hold', color: 'bg-amber-500/10 text-amber-500 border-amber-500/30', icon: Pause },
  completed: { label: 'Completed', color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/10 text-red-500 border-red-500/30', icon: AlertCircle }
};

const projectColors = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

const emptyProject = {
  name: '',
  client_name: '',
  description: '',
  status: 'active',
  start_date: new Date().toISOString().split('T')[0],
  end_date: '',
  budget: '',
  hourly_rate: '',
  color: '#3b82f6',
  notes: ''
};

const emptyTimeEntry = {
  date: new Date().toISOString().split('T')[0],
  hours: '',
  description: '',
  billable: true
};

export default function Projects() {
  const { isAuthenticated } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [showTimeForm, setShowTimeForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formData, setFormData] = useState(emptyProject);
  const [timeData, setTimeData] = useState(emptyTimeEntry);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [clients, setClients] = useState([]);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/business/projects`, {
        withCredentials: true
      });
      setProjects(response.data.projects || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/business/clients`, {
        withCredentials: true
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, [fetchProjects, fetchClients]);

  const handleCreateNew = () => {
    setEditingProject(null);
    setFormData(emptyProject);
    setShowForm(true);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name || '',
      client_id: project.client_id || '',
      client_name: project.client_name || '',
      description: project.description || '',
      status: project.status || 'active',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      budget: project.budget || '',
      hourly_rate: project.hourly_rate || '',
      color: project.color || '#3b82f6',
      notes: project.notes || ''
    });
    setShowForm(true);
    setSelectedProject(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    if (!formData.name) {
      toast.error('Project name is required');
      return;
    }

    try {
      setSaving(true);
      
      const dataToSend = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : 0,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : 0
      };
      
      if (editingProject) {
        await axios.put(`${API_URL}/api/business/projects/${editingProject.id}`, dataToSend, {
          withCredentials: true
        });
        toast.success('Project updated');
      } else {
        await axios.post(`${API_URL}/api/business/projects`, dataToSend, {
          withCredentials: true
        });
        toast.success('Project created');
      }
      
      setShowForm(false);
      setFormData(emptyProject);
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      toast.error('Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await axios.delete(`${API_URL}/api/business/projects/${projectId}`, {
        withCredentials: true
      });
      toast.success('Project deleted');
      fetchProjects();
      setSelectedProject(null);
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleLogTime = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    if (!timeData.hours || parseFloat(timeData.hours) <= 0) {
      toast.error('Please enter hours');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/business/projects/${selectedProject.id}/time`, {
        ...timeData,
        hours: parseFloat(timeData.hours)
      }, { withCredentials: true });
      
      toast.success('Time logged');
      setShowTimeForm(false);
      setTimeData(emptyTimeEntry);
      fetchProjects();
    } catch (error) {
      toast.error('Failed to log time');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/business/projects/${projectId}`, { status: newStatus }, {
        withCredentials: true
      });
      toast.success(`Project marked as ${newStatus}`);
      fetchProjects();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredProjects = projects.filter(p => {
    if (activeTab === 'active') return p.status === 'active';
    if (activeTab === 'completed') return p.status === 'completed';
    return true;
  });

  if (loading && projects.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground mt-1">
            Track profitability and costs per project
          </p>
        </div>
        <Button onClick={handleCreateNew} data-testid="add-project-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Project
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Projects</p>
                <p className="text-2xl font-bold">{summary.total_projects || 0}</p>
              </div>
              <FolderKanban className="w-8 h-8 text-muted-foreground/40" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-blue-500">{summary.active_projects || 0}</p>
              </div>
              <Play className="w-8 h-8 text-blue-500/40" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Revenue</p>
                <p className="text-2xl font-bold text-emerald-500">
                  {formatCurrency(summary.total_income || 0)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-500/40" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expenses</p>
                <p className="text-2xl font-bold text-red-500">
                  {formatCurrency(summary.total_expenses || 0)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-red-500/40" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit</p>
                <p className="text-2xl font-bold text-purple-500">
                  {formatCurrency(summary.total_profit || 0)}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-500/40" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Form */}
      {showForm && (
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {editingProject ? 'Edit Project' : 'New Project'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Project Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Website Redesign"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client">Client</Label>
                  {clients.length > 0 ? (
                    <select
                      id="client"
                      value={formData.client_id || ''}
                      onChange={(e) => {
                        const client = clients.find(c => c.id === e.target.value);
                        setFormData({
                          ...formData, 
                          client_id: e.target.value,
                          client_name: client ? (client.company || client.name) : ''
                        });
                      }}
                      className="w-full px-3 py-2 rounded-lg border bg-background"
                    >
                      <option value="">-- Select Client --</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.company || c.name}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                      placeholder="Client name"
                    />
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Project details..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg border bg-background"
                  >
                    <option value="planning">Planning</option>
                    <option value="active">Active</option>
                    <option value="on_hold">On Hold</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex gap-1 flex-wrap">
                    {projectColors.map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`w-6 h-6 rounded-full border-2 ${
                          formData.color === color ? 'border-foreground' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({...formData, color})}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="budget">Budget ($)</Label>
                  <Input
                    id="budget"
                    type="number"
                    step="0.01"
                    value={formData.budget}
                    onChange={(e) => setFormData({...formData, budget: e.target.value})}
                    placeholder="10000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hourly_rate">Hourly Rate ($)</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    step="0.01"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                    placeholder="150"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingProject ? 'Update' : 'Create Project'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Time Entry Form */}
      {showTimeForm && selectedProject && (
        <Card className="border-blue-500/20 bg-blue-500/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                Log Time to {selectedProject.name}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowTimeForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogTime} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="time_date">Date</Label>
                  <Input
                    id="time_date"
                    type="date"
                    value={timeData.date}
                    onChange={(e) => setTimeData({...timeData, date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hours">Hours *</Label>
                  <Input
                    id="hours"
                    type="number"
                    step="0.25"
                    value={timeData.hours}
                    onChange={(e) => setTimeData({...timeData, hours: e.target.value})}
                    placeholder="2.5"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Billable?</Label>
                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      checked={timeData.billable}
                      onChange={(e) => setTimeData({...timeData, billable: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Yes</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="time_description">Description</Label>
                <Input
                  id="time_description"
                  value={timeData.description}
                  onChange={(e) => setTimeData({...timeData, description: e.target.value})}
                  placeholder="What did you work on?"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowTimeForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Log Time
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Tabs & List */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({projects.length})</TabsTrigger>
          <TabsTrigger value="active">
            Active ({projects.filter(p => p.status === 'active').length})
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed ({projects.filter(p => p.status === 'completed').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {filteredProjects.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FolderKanban className="w-12 h-12 text-muted-foreground/30 mb-4" />
                <p className="text-lg font-medium mb-2">No projects found</p>
                <Button onClick={handleCreateNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Project
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {filteredProjects.map((project) => {
                const status = statusConfig[project.status] || statusConfig.active;
                const StatusIcon = status.icon;
                const fin = project.financials || {};
                const budgetUsed = fin.budget_used || 0;
                
                return (
                  <Card
                    key={project.id}
                    className={`cursor-pointer hover:border-primary/30 transition-colors ${
                      selectedProject?.id === project.id ? 'border-primary' : ''
                    }`}
                    onClick={() => setSelectedProject(selectedProject?.id === project.id ? null : project)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div 
                            className="w-12 h-12 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${project.color}20` }}
                          >
                            <FolderKanban className="w-6 h-6" style={{ color: project.color }} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-semibold">{project.name}</p>
                              <Badge variant="outline" className={status.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {status.label}
                              </Badge>
                            </div>
                            {project.client_name && (
                              <p className="text-sm text-muted-foreground">{project.client_name}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${fin.net_profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            {formatCurrency(fin.net_profit || 0)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {fin.profit_margin || 0}% margin • {fin.total_hours || 0}h logged
                          </p>
                        </div>
                      </div>

                      {/* Budget Progress */}
                      {project.budget > 0 && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-muted-foreground mb-1">
                            <span>Budget Used</span>
                            <span>{formatCurrency(fin.total_expenses || 0)} / {formatCurrency(project.budget)}</span>
                          </div>
                          <Progress 
                            value={Math.min(100, budgetUsed)} 
                            className={`h-1.5 ${budgetUsed > 100 ? '[&>div]:bg-red-500' : ''}`}
                          />
                        </div>
                      )}

                      {/* Expanded View */}
                      {selectedProject?.id === project.id && (
                        <div className="mt-4 pt-4 border-t space-y-4">
                          {/* Financial Summary */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                              <p className="text-xs text-muted-foreground">Revenue</p>
                              <p className="text-lg font-bold text-emerald-500">{formatCurrency(fin.total_income)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                              <p className="text-xs text-muted-foreground">Expenses</p>
                              <p className="text-lg font-bold text-red-500">{formatCurrency(fin.total_expenses)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
                              <p className="text-xs text-muted-foreground">Hours</p>
                              <p className="text-lg font-bold text-blue-500">{fin.total_hours || 0}h</p>
                            </div>
                            <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/20">
                              <p className="text-xs text-muted-foreground">Effective Rate</p>
                              <p className="text-lg font-bold text-purple-500">${fin.effective_hourly_rate || 0}/hr</p>
                            </div>
                          </div>

                          {/* Dates */}
                          {(project.start_date || project.end_date) && (
                            <div className="flex gap-4 text-sm text-muted-foreground">
                              {project.start_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  Started: {formatDate(project.start_date)}
                                </span>
                              )}
                              {project.end_date && (
                                <span>Due: {formatDate(project.end_date)}</span>
                              )}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex flex-wrap gap-2">
                            <Button 
                              size="sm"
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setTimeData(emptyTimeEntry);
                                setShowTimeForm(true);
                              }}
                            >
                              <Clock className="w-4 h-4 mr-1" />
                              Log Time
                            </Button>
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(project); }}>
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            {project.status === 'active' && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-emerald-500"
                                onClick={(e) => { e.stopPropagation(); handleStatusChange(project.id, 'completed'); }}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Mark Complete
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-500"
                              onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
